/**
 * Permission keys: the SPA's copy of the server's catalog
 * (backend/app/security/permissions.py). A backend test holds the two equal,
 * key for key, so a typo here fails type-checking and a new key there fails CI.
 *
 * The server decides; the client only hides what the caller may not do. Gate
 * controls on these keys (`can` / `<Can>` from SessionContext), never on the
 * user's role.
 */

export type PermissionKey =
  | 'dashboard.view'
  | 'dashboard.attention.view'
  | 'idea.submit'
  | 'idea.view'
  | 'idea.export'
  | 'idea.research.rerun.own'
  | 'idea.research.rerun.any'
  | 'idea.triage'
  | 'idea.industry.change'
  | 'research.dimensions.view'
  | 'research.dimensions.manage'
  | 'research.prompt.view'
  | 'research.prompt.manage'
  | 'research.matrix.view'
  | 'research.matrix.manage'
  | 'mcp.token.manage.own'
  | 'api.key.manage.own'
  | 'asset.view'
  | 'asset.view.draft'
  | 'asset.view.internal'
  | 'asset.manage'
  | 'asset.publish'
  | 'asset.archive'
  | 'asset.export'
  | 'advisor.view'
  | 'advisor.view.all'
  | 'advisor.run'
  | 'advisor.client.delete'
  | 'portfolio.view_demand'
  | 'industry.view'
  | 'industry.create'
  | 'industry.update'
  | 'industry.suggest'
  | 'industry.review'
  | 'user.view'
  | 'user.create'
  | 'user.provision'
  | 'user.update'
  | 'user.role.change'
  | 'user.status.change'
  | 'user.password.reset'
  | 'user.source.change'
  | 'access.permissions.view'
  | 'settings.view'
  | 'settings.manage'
  | 'ai.models.view'
  | 'ai.models.manage'
  | 'ai.models.test'
  | 'ai.processes.view'
  | 'ai.processes.manage'
  | 'ai.budget.view'
  | 'ai.budget.manage'
  | 'ai.usage.view'
  | 'audit.view'
  | 'audit.view.signin'
  | 'audit.tracing.manage'
  | 'feedback.submit'
  | 'feedback.view.own'
  | 'feedback.view.any'
  | 'feedback.comment.own'
  | 'feedback.comment.any'
  | 'feedback.cancel.own'
  | 'feedback.status.change';

/** Whether `held` includes any of `keys` (one key: whether it holds that one). */
export function holdsAny(held: ReadonlySet<string>, ...keys: PermissionKey[]): boolean {
  return keys.some((key) => held.has(key));
}

/** The items whose `permission` is held, in their order: tabs, menu entries. */
export function permitted<T extends { permission: PermissionKey }>(held: ReadonlySet<string>, items: readonly T[]): T[] {
  return items.filter((item) => held.has(item.permission));
}

/** The open tab: the one asked for when it is among `visible`, else the first visible one. */
export function openTab<T extends { key: string }>(visible: readonly T[], asked: string | null): T | undefined {
  return visible.find((tab) => tab.key === asked) ?? visible[0];
}
